import * as React from 'react'

import get from 'lodash/get'
import NextApp from 'next/app'
import getConfig from 'next/config'
import Error from 'next/error'
import NextHead from 'next/head'

import { withSentry } from '@grandvision/analytics'
import { AuthProvider, Routes, Types as AuthTypes } from '@grandvision/auth'
import {
  BloomreachApi,
  BloomreachCmsProvider,
  BloomreachCmsSettings,
  BloomreachService,
  getClientSession,
  getServerSession,
  RuntimeContext,
  Session,
} from '@grandvision/bloomreach'
import {
  GoogleMapsProvider,
  GoogleMapsSettings,
  Labels,
  LabelsObject,
  MarkerClusters,
  MediaQuery,
  Pin,
  ToastContainer,
} from '@grandvision/common'
import { DeliveryIndicatorProvider } from '@grandvision/inventory'
import { withRedux } from '@grandvision/next'
import {
  ApiConfigProvider,
  Countries,
  Currencies,
  DistanceUnit,
  IntlProvider,
  IntlSettings,
  Languages,
  mergeLabels,
  ReduxProvider,
  StoreWithPersistor,
} from '@grandvision/state'

import { isMultilingualEnabled, isMyAccountEnabled } from '../util/feature-toggle'

import '../styles/index.scss'

export interface NextProcess extends NodeJS.Process {
  browser: boolean
}

const { publicRuntimeConfig } = getConfig()

const cmsService = BloomreachService(isMultilingualEnabled())

const googleMapsSettings: GoogleMapsSettings = {
  apiKey: publicRuntimeConfig.googleMaps.apiKey,
  defaultCenter: publicRuntimeConfig.googleMaps.mapCenter,
  defaultZoom: 7,
  pin: Pin({
    themes: {
      primary: { foreground: '#ffffff', background: '#009cde' },
      highlight: { foreground: '#ffffff', background: '#009cde' },
    },
  }),
  markerClusterOptions: MarkerClusters({
    theme: {
      color: '#009cde',
      textColor: '#ffffff',
    },
    minimumClusterSize: 3,
    maxZoom: 10,
  }),
}

type Props = {
  pageProps: any
  store: StoreWithPersistor
  labels: BloomreachApi.LabelResources
  errorFlag: boolean
  bloomreachCmsSettings: BloomreachCmsSettings
  locale: string
  initialBreakpoint: MediaQuery.BreakpointName
}

// My Account Routes
const routes: Routes = {
  createAccount: 'create',
  login: 'login',
  logout: 'logout',
  orders: 'orders',
  signup: 'signup',
  privacy: 'privacy',
  profile: 'profile',
  addressBook: 'address-book',
  resetPassword: 'reset-password',
  linkExpired: 'link-expired',
  setPassword: 'set-password',
  unlinkAppointment: 'unlink-appointment',
  unlinkOrder: 'unlink-order',
}

const {
  deliveryIndicator,
  grandvision: {
    apiUrl,
    myAccount: { auth0Domain, auth0ClientId, auth0CustomDomain },
  },
} = publicRuntimeConfig

const authSettings = {
  apiUrl,
  basePath: 'my-account',
  clientId: auth0ClientId,
  customDomain: auth0CustomDomain,
  domain: auth0Domain,
  routes,
  checkoutRoute: '/checkout/shipping',
}

class App extends NextApp<Props> {
  static async getInitialProps({ Component, ctx }) {
    const defaultLocale = get(publicRuntimeConfig, 'defaultLocale', 'it-IT')
    const locale =
      ctx.req && ctx.req.locale
        ? `${ctx.req.locale.language}-${ctx.req.locale.region}`
        : defaultLocale

    const nextProcess = process && (process as NextProcess)
    // TODO Incorporate this in AppContext (to be created).
    const session: Session =
      nextProcess && nextProcess.browser ? getClientSession(ctx.req) : getServerSession(ctx.req)

    cmsService.setCmsUrls(publicRuntimeConfig.bloomreach.cmsUrls)
    const preview = cmsService.isCmsPreview(session.search)

    // the "*" fetches all available label resources
    const tasks = [
      cmsService.fetchCmsResource(['*'], locale, publicRuntimeConfig.bloomreach.cmsUrls),
    ]

    const query = (ctx || {}).query || {}
    const hasPageModel = !!query.pageModel
    const request = { hostname: session.host, path: session.path || '/' }

    let errorFlag = false
    let data = ctx

    if (!hasPageModel) {
      try {
        const pageModel = await cmsService.fetchCmsPage(request, locale, session.cookie)
        const pageMeta = cmsService.getPageMeta(pageModel!)

        data = {
          ...ctx,
          query: {
            search: query,
            request,
            preview,
            pageModel,
            locale,
            ...pageMeta,
          },
        }
      } catch (err) {
        console.error(err)
        errorFlag = true
      }
    }

    if (Component.getInitialProps && typeof Component.getInitialProps === 'function') {
      tasks.push(Component.getInitialProps.call(null, data))
    }

    const [resourceResult, pageProps] = await Promise.all(tasks)

    return {
      bloomreachCmsSettings: query.bloomreachCmsSettings,
      pageProps: pageProps || {},
      labels: resourceResult || {},
      errorFlag,
      locale,
      initialBreakpoint: ctx.req && ctx.req.mediaQueryBreakpoint,
    }
  }

  render() {
    const {
      Component,
      pageProps,
      store,
      labels = {},
      errorFlag,
      bloomreachCmsSettings,
      locale,
      initialBreakpoint,
    } = this.props

    if (errorFlag) {
      return <Error statusCode={500} labels={labels} />
    }

    // omit `bloomreach` from `publicRuntimeConfig`
    const { bloomreach, ...cleanPublicRuntimeConfig } = publicRuntimeConfig

    // My account opt ins
    const optIns: AuthTypes.OptInItem[] = [
      {
        title: Labels.getLabel(labels.account || {}, 'optIn.marketingEmails'),
        value: 'marketingEmails',
        isInSignup: true,
      },
      {
        title: Labels.getLabel(labels.account || {}, 'optIn.marketingPost'),
        value: 'directMail',
      },
      {
        title: Labels.getLabel(labels.account || {}, 'optIn.marketingSMS'),
        value: 'marketingSMS',
      },
      {
        title: Labels.getLabel(labels.account || {}, 'optIn.marketingPhone'),
        value: 'teleMarketing',
      },
    ]

    const mergedLabels = mergeLabels(labels as any)
    const [language, country] = locale.split('-')
    const currency: Currencies = get(publicRuntimeConfig, 'currency', Currencies.EUR)

    const intlSettings: IntlSettings = {
      language: Languages[language],
      country: Countries[country],
      currency,
      distanceUnit: DistanceUnit.km,
    }

    return (
      <React.StrictMode>
        <BloomreachCmsProvider settings={bloomreachCmsSettings}>
          <ReduxProvider store={store}>
            <IntlProvider settings={intlSettings} labels={mergedLabels}>
              <ApiConfigProvider apiConfig={publicRuntimeConfig.grandvision}>
                <GoogleMapsProvider settings={googleMapsSettings}>
                  <DeliveryIndicatorProvider settings={deliveryIndicator}>
                    <MediaQuery.Provider initialBreakpoint={initialBreakpoint}>
                      <AuthProvider
                        enabled={isMyAccountEnabled()}
                        settings={{ ...authSettings, optIns }}
                      >
                        <RuntimeContext.Provider
                          value={{ ...cleanPublicRuntimeConfig, headComponent: NextHead }}
                        >
                          <ToastContainer labels={labels as LabelsObject} />
                          <Component {...pageProps} labels={labels} store={store} />
                        </RuntimeContext.Provider>
                      </AuthProvider>
                    </MediaQuery.Provider>
                  </DeliveryIndicatorProvider>
                </GoogleMapsProvider>
              </ApiConfigProvider>
            </IntlProvider>
          </ReduxProvider>
        </BloomreachCmsProvider>
      </React.StrictMode>
    )
  }
}

export default withSentry(withRedux(App))
